import { Injectable } from '@angular/core';
import { APIService } from './api.service';
import { Observable, timer } from 'rxjs';
import { map, switchMap, share } from 'rxjs/operators';
import { CodeNameObj } from '../models/generic';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';

// @dynamic
export class DepartureAirport extends CodeNameObj {
    children: DepartureAirport[] = null;
    constructor(data) {
        super();
        this.code = data.code;
        this.name = data.name;
        if (data.children && data.children.length) {
            this.children = data.children.map(child => new DepartureAirport(child));
            if (!this.code) {
                this.code = this.children.map(airport => airport.code).join(',');
            }
        }
    }

    static find(airports: DepartureAirport[], matcher: Function) {
        let result: DepartureAirport;
        const findAirport = airport => {
            if (matcher(airport)) {
                result = airport;
                return true;
            } else if (airport.children) {
                return airport.children.some(findAirport);
            }
            return false;
        };
        if (airports && matcher) {
            airports.some(findAirport);
        }
        return result;
    }

    static findByCode(airports: DepartureAirport[], code: string): DepartureAirport {
        if (code.indexOf(',') > -1) {
            return DepartureAirport.find(airports, airport => airport.code === code);
        } else {
            return DepartureAirport.getSingleAirportByCode(airports, code);
        }
    }

    static getSingleAirportByCode(airports: DepartureAirport[], code: string): DepartureAirport {
        if (!airports || code.indexOf(',') > -1) {
            return null;
        }
        for (let i = 0; i < airports.length; i++) {
            if (airports[i].children) {
                for (let j = 0; j < airports[i].children.length; j++) {
                    const childAirport = airports[i].children[j];
                    if (childAirport && childAirport.code === code) {
                        return childAirport;
                    }
                }
            } else {
                if (airports[i].code === code) {
                    return airports[i];
                }
            }
        }
        return null;
    }

    static findByName(airports: DepartureAirport[], name: string): DepartureAirport {
        return DepartureAirport.find(airports, airport => airport.name === name);
    }

    static findClosestMatchingAirportGroupByCodes(departureAirports: DepartureAirport[], codes: string) {
        if (!departureAirports || !codes || codes.indexOf(',') < 0) {
            return null;
        }
        const originalSelectedAirportCodes = codes.split(',');
        if (!originalSelectedAirportCodes) {
            return null;
        }
        const matchedAirports: { airport: DepartureAirport, numberOfMatches: number }[] = [];
        // For each departure code
        for (let index = 0; index < departureAirports.length; index++) {
            const departureAirport = departureAirports[index];
            let numberOfMatches = 0;
            // If the departure airport is a group
            if (departureAirport.children) {
                // For each child airport in the group, look for a matching code
                departureAirport.children.forEach(childAirport => {
                    if (originalSelectedAirportCodes.indexOf(childAirport.code) > -1) {
                        numberOfMatches = numberOfMatches + 1;
                    }
                });
                // If there is a match, add airport to the matchedAirports array
                if (numberOfMatches > 0) {
                    matchedAirports.push({
                        airport: departureAirport,
                        numberOfMatches: numberOfMatches
                    });
                }
            }
        }
        if (matchedAirports.length === 0) {
            return null;
        }
        // Sort array by strongest match
        matchedAirports.sort((a, b) => a.numberOfMatches > b.numberOfMatches ? -1 : 1);
        return matchedAirports[0].airport;
    }
}

@Injectable()
export class SearchService {

    constructor(private apiService: APIService) {
    }

    getWhere(baseURL): Observable<{ destinations, categories }> {
        return this.apiService.get('/api/v1/json/packages/destinations', baseURL).pipe(
            map(result => {
                const byName = (a, b) => a.name < b.name ? -1 : 1;
                const destinations = result.destinations.map(destination => ({ ...destination, type: 'destination' }));
                const categories = result.categories.map(name => ({ name, code: name, type: 'category' }));
                destinations.sort(byName);
                categories.sort(byName);
                return {
                    destinations,
                    categories
                };
            })
        );
    }

    // getFrom(baseURL): Observable<any> {
    //     return this.apiService.get('/api/v1/json/packages/airports', '', baseURL).pipe(
    //         map(airportMap => Object.keys(airportMap).map((groupName) => ({
    //             name: groupName,
    //             children: airportMap[groupName]
    //         })))
    //     );
    // }

    getFrom(baseURL): Observable<any> {
        return this.apiService.get('/api/v1/json/packages/airports/list', '', baseURL);
    }

    getDestinationsByRoute(airports, baseURL): Observable<any> {
        return this.apiService.get('/api/v1/json/packages/destinations/routes/' + airports, '', baseURL).pipe(
            map(result => {
                return result;
            })
        );
    }

    getHotels(name, departureAirports, baseURL): Observable<any> {
        let airports = '';
        if (departureAirports.codes.length) {
            for (let index = 0; index < departureAirports.codes.length; index++) {            // airports.concat(this.selectedAirport.codes[index]);
                airports = airports + departureAirports.codes[index];
                if (departureAirports.codes.length !== index + 1) {
                    airports = airports + ',';
                }
            }
        }
        return this.apiService.get('/api/v1/json/packages/search/hotels/' + name + '/' + airports, '', baseURL).pipe(
            map(result => {
                return result;
            }),
            share()
        );
    }

    searchHotels(term, departureAirports) {
        let airports = '';
        if (departureAirports.codes.length) {
            for (let index = 0; index < departureAirports.codes.length; index++) {            // airports.concat(this.selectedAirport.codes[index]);
                airports = airports + departureAirports.codes[index];
                if (departureAirports.codes.length !== index + 1) {
                    airports = airports + ',';
                }
            }
        }
        let listOfBooks = this.apiService.get('/api/v1/json/packages/search/hotels/' + term + '/' + airports)
            .pipe(
                debounceTime(500),  // WAIT FOR 500 MILISECONDS ATER EACH KEY STROKE.
                map(
                    (data: any) => {
                        let hotels = [];
                        for (let i = 0; i < data.length; i++) {
                            const country = data[i];
                            for (let j = 0; j < country.regions.length; j++) {
                                const region = country.regions[j];
                                for (let k = 0; k < region.resorts.length; k++) {
                                    const resort = region.resorts[k];
                                    for (let l = 0; l < resort.hotels.length; l++) {
                                        const hotel = resort.hotels[l];
                                        let hotelObj = {
                                            name: hotel,
                                            type: 'hotel',
                                            resort: resort.name,
                                            region: region.name,
                                            country: country.name,
                                            label: hotel
                                        };
                                        if (resort.name) {
                                            hotelObj.label = hotelObj.label + ', ' + resort.name;
                                        }
                                        if (region.name) {
                                            if (!resort.name || resort.name !== region.name) {
                                                hotelObj.label = hotelObj.label + ', ' + region.name;
                                            }
                                        }
                                        if (country.name) {
                                            hotelObj.label = hotelObj.label + ', ' + country.name;
                                        }
                                        hotels.push(hotelObj);
                                    }
                                }
                            }
                        }
                        return (
                            hotels
                        );
                    }
                ));
        return listOfBooks;
    }

    performSearch(baseURL, startDate: string, endDate: string, departures?: any[], where?: any[], passengers?: any, duration?: number, starRatings?: any[]): Observable<any[]> {
        const params: any = {
            startDate,
            endDate
        };
        params.departureCodes = departures.map(departure => departure.code);
        params.destinations = JSON.stringify(where);
        params.passengers = JSON.stringify(passengers);
        params.duration = duration;
        params.occupancy = JSON.stringify(passengers);
        return this.apiService.get('search/dp', params, baseURL).pipe(
            map(results => {
                return results;
            })
        );
    }

    getOccupancy(passengers) {
        let adults = 0;
        let children = 0;
        passengers.forEach(element => {
            adults += element.adults;
            children += element.children;
        });

        return {
            'adults': adults,
            'children': children,
            'infants': 0
        };
    }

    formatDestination(destination) {
        let destinationString = '';

        if (destination.hotel) {
            destinationString = destination.hotel;
        }

        if (!destination.hotel && destination.resort) {
            destinationString = destination.resort;
        }

        if (!destination.resort && destination.country) {
            destinationString += ' ' + destination.country;
        }

        return destinationString.replace(/^\s+/g, '');
    }
}
